import React, { useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Loader } from '@/ui/components/Loader';
import { useTranslate } from '@/translations';
import { templateScopeLabel } from '@/modules/SystemAdministration/Jobs/JobChainTemplates/templateLabels';
import {
  jobChainTemplates,
  useCacheTemplateDetail,
  useJobChainTemplateMetadata,
  unwrapTemplateResponse,
  type JobChainTemplateScope,
} from '@/api/JobChainTemplates';
import {
  EditorShell,
  templateFailureText,
  templateListPath,
} from '@/modules/SystemAdministration/Jobs/JobChainTemplates/editorShared';

export const NewJobChainTemplate = () => {
  const { t } = useTranslate();
  const metadataQuery = useJobChainTemplateMetadata();
  const metadata = metadataQuery.data;
  const [scope, setScope] = useState<JobChainTemplateScope | ''>('');
  const [customer, setCustomer] = useState('');
  const [type, setType] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const errorRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { search } = useLocation();
  const cacheDetail = useCacheTemplateDetail();
  const scopes =
    metadata?.authorizedScopes.filter((value) =>
      value === 'Global'
        ? metadata.capabilities.canCreateGlobal
        : metadata.capabilities.canCreateCustomer,
    ) || [];
  if (!metadata && !metadataQuery.isError)
    return (
      <div
        role="status"
        aria-label={t('jobChainTemplatesLoadingCreation')}
        aria-busy="true"
      >
        <Loader />
      </div>
    );
  return (
    <EditorShell>
      <Link to={`${templateListPath}${search}`}>
        {t('jobChainTemplatesBackToTemplates')}
      </Link>
      <h1>{t('jobChainTemplatesCreateTitle')}</h1>
      {metadataQuery.isError && (
        <div role="alert">
          {templateFailureText(metadataQuery.error, t)}
          <button onClick={() => void metadataQuery.refetch()}>
            {t('jobChainTemplatesRetryMetadata')}
          </button>
        </div>
      )}
      {error && (
        <div role="alert" tabIndex={-1} ref={errorRef}>
          {error}
        </div>
      )}
      {metadata &&
        (scopes.length === 0 ? (
          <p role="status">{t('jobChainTemplatesCreateDenied')}</p>
        ) : (
          <form
            onSubmit={async (event) => {
              event.preventDefault();
              if (
                !scopes.includes(scope as JobChainTemplateScope) ||
                !metadata.jobChainTypes.some((item) => item.value === type) ||
                (scope === 'Customer' &&
                  !metadata.customers.some(
                    (item) => item.id === Number(customer),
                  ))
              ) {
                setError(t('jobChainTemplatesCreateSelectionRequired'));
                return;
              }
              setPending(true);
              setError('');
              try {
                const result = unwrapTemplateResponse(
                  await jobChainTemplates.create({
                    scope: scope as JobChainTemplateScope,
                    ...(scope === 'Customer' && {
                      customerId: Number(customer),
                    }),
                    jobChainType: type,
                  }),
                );
                await cacheDetail(result);
                navigate(`${templateListPath}/${result.id}/edit${search}`);
              } catch (failure) {
                setError(templateFailureText(failure, t));
                setTimeout(() => errorRef.current?.focus(), 0);
              } finally {
                setPending(false);
              }
            }}
          >
            <fieldset disabled={pending}>
              <legend>{t('jobChainTemplatesCreateDetails')}</legend>
              <label>
                {t('jobChainTemplatesFilterScope')}
                <select
                  required
                  value={scope}
                  onChange={(event) => {
                    setScope(event.target.value as JobChainTemplateScope);
                    setCustomer('');
                  }}
                >
                  <option value="">{t('jobChainTemplatesSelectScope')}</option>
                  {scopes.map((value) => (
                    <option key={value} value={value}>
                      {templateScopeLabel(value, t)}
                    </option>
                  ))}
                </select>
              </label>
              {scope === 'Customer' && (
                <label>
                  {t('jobChainTemplatesFilterCustomer')}
                  <select
                    required
                    value={customer}
                    onChange={(event) => setCustomer(event.target.value)}
                  >
                    <option value="">
                      {t('jobChainTemplatesSelectCustomer')}
                    </option>
                    {metadata.customers.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label>
                {t('jobChainTemplatesFilterType')}
                <select
                  required
                  value={type}
                  onChange={(event) => setType(event.target.value)}
                >
                  <option value="">{t('jobChainTemplatesSelectType')}</option>
                  {metadata.jobChainTypes.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
              <button data-primary="true" type="submit">
                {t(
                  pending
                    ? 'jobChainTemplatesCreating'
                    : 'jobChainTemplatesCreateAction',
                )}
              </button>
            </fieldset>
          </form>
        ))}
    </EditorShell>
  );
};
